import { cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useGroups } from '../../../hooks/use-groups';
import { GroupPicker, GroupPickerItem } from '../GroupPicker';

export interface EntityGroupPickerProps {
  value: GroupPickerItem<number>[] | null;
  onChange: (value: GroupPickerItem<number>[]) => void;
  multiple?: boolean;
  entity: Entities;
  expanded?: string[];
  onNodeToggle?: (event: React.SyntheticEvent, nodeIds: string[]) => void;
  className?: string;
  includeNoneGroupOption?: boolean;
}

export function EntityGroupPicker(props: EntityGroupPickerProps) {
  const {
    value,
    onChange,
    entity,
    expanded,
    onNodeToggle,
    className,
    includeNoneGroupOption = false,
  } = props;

  const { t } = useTranslation();

  const response = useGroups(entity);

  const groups = useMemo(() => {
    let groupsResult = [];
    if (entity !== Entities.userGroup) {
      groupsResult = response.data?.groups ?? null;
    } else if (response.data?.groups) {
      const cloned = cloneDeep(response.data.groups);
      groupsResult = cloned
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((x, index) => ({ ...x, index }));
    }

    if (includeNoneGroupOption) {
      groupsResult = [
        {
          id: '0',
          name: t('COMMON.NONE'),
          parentId: 0,
          index: 0,
        },
        ...(groupsResult ?? []).map((x, index) => ({
          ...x,
          index: index + 1,
        })),
      ];
    }

    return groupsResult;
  }, [entity, response.data?.groups, includeNoneGroupOption, t]);

  return (
    <GroupPicker
      {...props}
      value={value}
      onChange={(values: GroupPickerItem<number>[]) => {
        const uniqueValues = values.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );
        onChange(uniqueValues);
      }}
      groups={groups}
      multiple={props.multiple}
      expanded={expanded}
      onNodeToggle={onNodeToggle}
      className={className}
    />
  );
}

export const TextBuildingBlockGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.textBuildingBlockGroup} {...props} />;

export const ProjectGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.projectGroup} {...props} />;

export const ArticleGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.articleGroup} {...props} />;

export const CustomerGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.customerGroup} {...props} />;

export const SupplierGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.supplierGroup} {...props} />;

export const UserGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.userGroup} {...props} />;

export const ServiceContractGroupPicker = (
  props: Omit<EntityGroupPickerProps, 'entity'>
) => <EntityGroupPicker entity={Entities.contractGroup} {...props} />;
